import { useMemo, useState, useEffect, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import BaseLayout from "@/v2/components/BaseLayout";
import { searchArticles } from "@/v2/queries";
import { V2_CATEGORIES, V2_CATEGORY_LABELS, type V2Category } from "@/v2/categories";
import { decodeEntities } from "@/v2/textUtils";
import { labelFor } from "@/v2/categories";

function isCat(x: string | null): x is V2Category {
  return !!x && (V2_CATEGORIES as readonly string[]).includes(x);
}

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const q = params.get("q") ?? "";
  const catParam = params.get("cat");
  const cat = isCat(catParam) ? catParam : undefined;

  const [input, setInput] = useState(q);
  useEffect(() => setInput(q), [q]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["v2", "search", q, cat ?? ""],
    queryFn: () => searchArticles(q, { category: cat, limit: 50 }),
    enabled: q.trim().length > 0,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const term = input.trim();
    const next = new URLSearchParams();
    if (term) next.set("q", term);
    if (cat) next.set("cat", cat);
    setParams(next);
  };

  const setCat = (c?: V2Category) => {
    const next = new URLSearchParams();
    if (q) next.set("q", q);
    if (c) next.set("cat", c);
    setParams(next);
  };

  const title = q
    ? `Sök: ${q} – Korthet`
    : "Sök nyhetssammanfattningar – Korthet";

  const hits = data ?? [];

  return (
    <BaseLayout
      title={title}
      description="Sök bland Korthets AI-sammanfattade nyheter."
      canonicalPath="/sok"
      noindex
    >
      <h1 className="k2-h1">Sök</h1>

      <form className="k2-search k2-search-page" role="search" onSubmit={submit}>
        <input
          type="search"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Sökord…"
          autoFocus
        />
        <button type="submit">Sök</button>
      </form>

      {q && (
        <div className="k2-search-filters">
          <button
            type="button"
            className={!cat ? "is-active" : ""}
            onClick={() => setCat(undefined)}
          >
            Alla kategorier
          </button>
          {V2_CATEGORIES.map((c) => (
            <button
              key={c}
              type="button"
              className={cat === c ? "is-active" : ""}
              onClick={() => setCat(c)}
            >
              {V2_CATEGORY_LABELS[c]}
            </button>
          ))}
        </div>
      )}

      {!q && (
        <p className="k2-empty">Skriv ett sökord för att hitta artiklar.</p>
      )}
      {q && (isLoading || isFetching) && <p className="k2-loading">Söker…</p>}
      {error && <p className="k2-error">Kunde inte söka just nu.</p>}
      {q && !isLoading && hits.length === 0 && (
        <p className="k2-empty">Inga träffar för ”{q}”.</p>
      )}

      {hits.length > 0 && (
        <ul className="k2-feed">
          {hits.map((h) => {
            const path = h.slug ? `/${h.category}/${h.slug}` : "#";
            const summary = decodeEntities(h.aiSummary || h.excerpt || "");
            const truncated =
              summary.length > 200 ? summary.slice(0, 197).trimEnd() + "…" : summary;
            return (
              <li key={h.id} className="k2-card">
                <h2 className="k2-card-title">
                  <Link to={path}>{decodeEntities(h.title)}</Link>
                </h2>
                {truncated && <p className="k2-card-excerpt">{truncated}</p>}
                <div className="k2-meta">
                  <span className="k2-cat-badge" data-cat={h.category}>
                    {labelFor(h.category as V2Category)}
                  </span>
                  {h.source && <span className="k2-source">{h.source}</span>}
                  {h.publishedAt && (
                    <time dateTime={h.publishedAt}>
                      {new Date(h.publishedAt).toLocaleDateString("sv-SE", {
                        day: "numeric",
                        month: "short",
                      })}
                    </time>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </BaseLayout>
  );
}